import React from 'react';

import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import pluralize from 'pluralize';
import { useDispatch } from 'react-redux';

import { MediaItem } from '../../../openapi/leo';
import {
  setShareablesConfig,
  setShowShareablesModal,
} from '../../../slices/LeoSlice';
import { AppDispatch } from '../../../types';
import { DEFAULT_SHOWN_PROPERTY_POSTERS_COUNT } from '../../../utils/LeoUtils';
import { cn } from '../../../utils/classUtils';

import './shareables.css';

type NeoLeoGenericShareablesProps = {
  msgId: string;
  shareables: MediaItem[];
};

const NeoLeoGenericShareables: React.FC<NeoLeoGenericShareablesProps> = ({
  msgId,
  shareables,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [currentPage, setCurrentPage] = React.useState<number>(0);
  const pageSize = DEFAULT_SHOWN_PROPERTY_POSTERS_COUNT;
  const totalCount = shareables ? shareables.length : 0;
  const lastPage = Math.ceil(totalCount / pageSize);

  const goToPage = (page: number) => setCurrentPage(page);

  const onPrevious = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    goToPage(currentPage - 1);
  };

  const onNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    goToPage(currentPage + 1);
  };

  const isLastPage = currentPage === lastPage - 1;
  const currentPageCount = isLastPage
    ? totalCount
    : (currentPage + 1) * pageSize;

  return (
    <>
      <div>
        <div
          className={cn(
            'flex flex-row justify-between items-center mb-2',
            'mb-4',
          )}
        >
          <p className='font-inter text-sm font-normal text-white'>
            Social Media {pluralize('Poster', totalCount)} ({currentPageCount}/
            {totalCount})
          </p>
          {totalCount > pageSize && (
            <div className='flex justify-between items-center'>
              <button
                className={cn(
                  currentPage === 0 ? 'text-gray-400' : 'text-white',
                  'disabled:cursor-not-allowed flex justify-center items-center cursor-pointer',
                )}
                onClick={(e) => {
                  e.preventDefault();
                  onPrevious(e);
                }}
                disabled={currentPage === 0}
                aria-label='go-previous'
              >
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  className='px-1 text-sm'
                />
              </button>

              <button
                className={cn(
                  isLastPage ? 'text-gray-400' : 'text-white',
                  'disabled:cursor-not-allowed flex justify-center items-center cursor-pointer',
                )}
                disabled={isLastPage}
                onClick={(e) => {
                  e.preventDefault();
                  onNext(e);
                }}
              >
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className='px-1 text-sm'
                />
              </button>
            </div>
          )}
        </div>
      </div>
      <div className={cn('flex gap-1 flex-wrap items-center', 'gap-2')}>
        {shareables
          .slice(currentPage * pageSize, currentPage * pageSize + pageSize)
          .map(({ media_url }, ind) => (
            <div
              key={media_url}
              className={cn('w-32 h-32 rounded-lg', 'w-50 h-50')}
            >
              <img
                id='poster-image'
                src={media_url}
                className='w-full h-full rounded-lg cursor-pointer'
                alt='poster'
                onClick={() => {
                  dispatch(
                    setShareablesConfig({
                      msgId,
                      activeShareableIndex: currentPage * pageSize + ind,
                    }),
                  );
                  dispatch(setShowShareablesModal(true));
                }}
              />
            </div>
          ))}
      </div>
    </>
  );
};

export default NeoLeoGenericShareables;
